import {
	IconAlertTriangle,
	IconCircleDot,
	IconClock,
	IconInbox,
	IconPlayerPause,
	IconRefresh,
	IconSend,
	IconTrash,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { SiteHeader } from "@/components/site-header";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { timeAgo } from "@/lib/time";
import { cleanEmptyParams } from "@/lib/url";
import { orpc, type Outputs } from "@/orpc/client";

type QueueMetricSnapshot = Outputs["observation"]["queues"]["listMetrics"]["queues"][number];
type QueueMetricsSearch = {
	live: boolean;
	queueArn?: string;
};

export const Route = createFileRoute("/_app/observation/queues")({
	component: Page,
	validateSearch: (search) => getValidQueueMetricsSearch(search),
});

function getBooleanSearchValue(value: unknown, defaultValue: boolean) {
	if (typeof value === "boolean") {
		return value;
	}
	if (typeof value !== "string") {
		return defaultValue;
	}
	if (value === "true" || value === "1") {
		return true;
	}
	if (value === "false" || value === "0") {
		return false;
	}

	return defaultValue;
}

function getValidQueueMetricsSearch(search: Record<string, unknown>): QueueMetricsSearch {
	return {
		live: getBooleanSearchValue(search.live, false),
		queueArn: typeof search.queueArn === "string" ? search.queueArn : undefined,
	};
}

function getQueueMetricsSearchParams(search: QueueMetricsSearch) {
	return cleanEmptyParams({
		live: search.live ? true : undefined,
		queueArn: search.queueArn,
	});
}

function formatCount(value: number | undefined) {
	if (value === undefined) {
		return "-";
	}

	return new Intl.NumberFormat("en-US", {
		maximumFractionDigits: 0,
	}).format(value);
}

function formatAge(seconds: number | undefined) {
	if (seconds === undefined) {
		return "-";
	}

	if (seconds < 60) {
		return `${Math.round(seconds)}s`;
	}

	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = Math.round(seconds % 60);

	if (minutes < 60) {
		return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
	}

	const hours = Math.floor(minutes / 60);
	const remainingMinutes = minutes % 60;

	return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

function sumValues(
	queues: Array<QueueMetricSnapshot>,
	getValue: (queue: QueueMetricSnapshot) => number | undefined,
) {
	return queues.reduce((total, queue) => total + (getValue(queue) ?? 0), 0);
}

function getOldestMessageAge(queues: Array<QueueMetricSnapshot>) {
	const ages = queues
		.map((queue) => queue.cloudwatch.oldestMessageAgeSeconds)
		.filter((value): value is number => value !== undefined);

	return ages.length > 0 ? Math.max(...ages) : undefined;
}

function MetricCard({
	icon: CardIcon,
	label,
	value,
}: {
	icon: React.ComponentType<{ className?: string }>;
	label: string;
	value: string;
}) {
	return (
		<div className="rounded-lg border px-4 py-3">
			<div className="text-muted-foreground flex items-center gap-2 text-sm">
				<CardIcon className="size-4" />
				{label}
			</div>
			<div className="mt-2 text-2xl font-semibold">{value}</div>
		</div>
	);
}

function Page() {
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const queueMetricsOptions = orpc.observation.queues.listMetrics.queryOptions({
		input: {},
	});
	const { data, error, isFetching, refetch } = useQuery({
		...queueMetricsOptions,
		refetchInterval: search.live ? 10_000 : false,
		refetchIntervalInBackground: false,
	});
	const updateSearch = React.useCallback(
		(partialSearch: Partial<QueueMetricsSearch>) => {
			navigate({
				replace: true,
				search: (previous) => {
					const nextSearch = {
						...getValidQueueMetricsSearch(previous as Record<string, unknown>),
						...partialSearch,
					};

					return getQueueMetricsSearchParams(nextSearch) as QueueMetricsSearch;
				},
			});
		},
		[navigate],
	);
	const handleQueueChange = React.useCallback(
		(value: string | null) => updateSearch({ queueArn: value || undefined }),
		[updateSearch],
	);
	const handleLiveChange = React.useCallback(
		(checked: boolean) => updateSearch({ live: checked }),
		[updateSearch],
	);
	const visibleQueues = React.useMemo(() => {
		const queues = data?.queues ?? [];

		return search.queueArn ? queues.filter((queue) => queue.queue.arn === search.queueArn) : queues;
	}, [data?.queues, search.queueArn]);
	const totals = React.useMemo(
		() => ({
			deleted: sumValues(visibleQueues, (queue) => queue.cloudwatch.messagesDeleted),
			delayed: sumValues(visibleQueues, (queue) => queue.attributes.delayed),
			inFlight: sumValues(visibleQueues, (queue) => queue.attributes.inFlight),
			oldestMessageAgeSeconds: getOldestMessageAge(visibleQueues),
			received: sumValues(visibleQueues, (queue) => queue.cloudwatch.messagesReceived),
			sent: sumValues(visibleQueues, (queue) => queue.cloudwatch.messagesSent),
			visible: sumValues(visibleQueues, (queue) => queue.attributes.visible),
		}),
		[visibleQueues],
	);
	const selectedQueueName =
		data?.queues.find((queue) => queue.queue.arn === search.queueArn)?.queue.name ?? "All queues";
	const metricWindow = data?.queues[0]?.cloudwatch;

	return (
		<>
			<SiteHeader
				breadcrumbs={[
					{ key: "observation", label: "Observation", linkProps: { to: "/observation/queues" } },
					{ key: "observation/queues", label: "Queues" },
				]}
			/>
			<div className="flex flex-1 flex-col">
				<div className="@container/main flex flex-1 flex-col gap-4 px-4 py-4 md:gap-6 md:px-6 md:py-6">
					<div className="flex flex-col gap-4">
						<div className="grid gap-3 @3xl/main:grid-cols-3 @6xl/main:grid-cols-7">
							<MetricCard
								icon={IconCircleDot}
								label="Visible"
								value={formatCount(totals.visible)}
							/>
							<MetricCard
								icon={IconPlayerPause}
								label="Processing"
								value={formatCount(totals.inFlight)}
							/>
							<MetricCard
								icon={IconAlertTriangle}
								label="Delayed"
								value={formatCount(totals.delayed)}
							/>
							<MetricCard
								icon={IconClock}
								label="Oldest"
								value={formatAge(totals.oldestMessageAgeSeconds)}
							/>
							<MetricCard icon={IconSend} label="Sent 1h" value={formatCount(totals.sent)} />
							<MetricCard
								icon={IconInbox}
								label="Received 1h"
								value={formatCount(totals.received)}
							/>
							<MetricCard icon={IconTrash} label="Deleted 1h" value={formatCount(totals.deleted)} />
						</div>

						<Alert>
							<IconAlertTriangle />
							<AlertTitle>Metrics-only observation</AlertTitle>
							<AlertDescription>
								This page does not call ReceiveMessage and does not inspect message bodies. SQS
								attributes are approximate, and CloudWatch metrics can lag by a few minutes.
							</AlertDescription>
						</Alert>

						{data?.warnings.map((warning) => (
							<Alert key={warning} variant="warning">
								<IconAlertTriangle />
								<AlertTitle>Queue configuration</AlertTitle>
								<AlertDescription>{warning}</AlertDescription>
							</Alert>
						))}

						{error ? (
							<Alert variant="destructive">
								<IconAlertTriangle />
								<AlertTitle>Unable to load SQS metrics</AlertTitle>
								<AlertDescription>
									{error instanceof Error ? error.message : String(error)}
								</AlertDescription>
							</Alert>
						) : null}
					</div>

					<div className="flex flex-col gap-3 rounded-lg border py-4">
						<div className="flex flex-col gap-3 px-4 @4xl/main:flex-row @4xl/main:items-center @4xl/main:justify-between">
							<div className="flex flex-col gap-3 @2xl/main:flex-row @2xl/main:items-center">
								<Select value={search.queueArn ?? ""} onValueChange={handleQueueChange}>
									<SelectTrigger className="w-full @2xl/main:w-64">
										<SelectValue placeholder="All queues">{selectedQueueName}</SelectValue>
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="">All queues</SelectItem>
										{data?.queues.map((queue) => (
											<SelectItem key={queue.queue.arn} value={queue.queue.arn}>
												{queue.queue.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<div className="text-muted-foreground text-sm">
									{data?.fetchedAt ? `Updated ${timeAgo(data.fetchedAt)}` : "Not loaded yet"}
								</div>
							</div>

							<div className="flex items-center justify-between gap-3 @4xl/main:justify-end">
								<div className="flex items-center gap-2">
									<Switch checked={search.live} onCheckedChange={handleLiveChange} />
									<Label className="text-sm">Live</Label>
								</div>
								<Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
									{isFetching ? <Spinner /> : <IconRefresh />}
									Refresh
								</Button>
							</div>
						</div>

						<div className="overflow-hidden px-4">
							<div className="overflow-hidden rounded-lg border">
								<Table>
									<TableHeader className="bg-muted sticky top-0 z-10">
										<TableRow>
											<TableHead>Queue</TableHead>
											<TableHead>Status</TableHead>
											<TableHead>Visible</TableHead>
											<TableHead>Processing</TableHead>
											<TableHead>Delayed</TableHead>
											<TableHead>Oldest</TableHead>
											<TableHead>Sent 1h</TableHead>
											<TableHead>Received 1h</TableHead>
											<TableHead>Deleted 1h</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{visibleQueues.length > 0 ? (
											visibleQueues.map((queue) => (
												<TableRow key={queue.queue.arn}>
													<TableCell>
														<div className="max-w-80 space-y-1">
															<div className="truncate font-medium">{queue.queue.name}</div>
															<div className="text-muted-foreground truncate text-xs">
																{queue.queue.url ?? queue.queue.arn}
															</div>
														</div>
													</TableCell>
													<TableCell>
														{queue.error ? (
															<Badge variant="destructive">Error</Badge>
														) : (
															<Badge variant="outline">Observed</Badge>
														)}
													</TableCell>
													<TableCell>{formatCount(queue.attributes.visible)}</TableCell>
													<TableCell>{formatCount(queue.attributes.inFlight)}</TableCell>
													<TableCell>{formatCount(queue.attributes.delayed)}</TableCell>
													<TableCell>
														{formatAge(queue.cloudwatch.oldestMessageAgeSeconds)}
													</TableCell>
													<TableCell>{formatCount(queue.cloudwatch.messagesSent)}</TableCell>
													<TableCell>{formatCount(queue.cloudwatch.messagesReceived)}</TableCell>
													<TableCell>{formatCount(queue.cloudwatch.messagesDeleted)}</TableCell>
												</TableRow>
											))
										) : (
											<TableRow>
												<TableCell colSpan={9}>
													<Empty className="w-full">
														<EmptyHeader>
															<EmptyTitle>No queues</EmptyTitle>
															<EmptyDescription>
																{data?.queues.length
																	? "No SQS queue matches the current filter."
																	: "No SQS queues are configured for observation."}
															</EmptyDescription>
														</EmptyHeader>
													</Empty>
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</div>
						</div>

						<div className="text-muted-foreground px-4 text-xs">
							CloudWatch window:{" "}
							{metricWindow
								? `${timeAgo(metricWindow.windowStart)} to ${timeAgo(metricWindow.windowEnd)}`
								: "last hour"}
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
